import React, { useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useGetList } from 'react-admin';

export interface TenantSelectorProps {
    onSelectTenant: (tenantId: string | null) => void;
    label?: string;
    variant?: 'outlined' | 'filled' | 'standard';
    size?: 'small' | 'medium';
    disabled?: boolean;
}

interface Tenant {
    id: string;
    name: string;
}

export const TenantSelector: React.FC<TenantSelectorProps> = ({
    onSelectTenant,
    label = 'Select Tenant',
    variant = 'outlined',
    size = 'medium',
    disabled = false
}) => {
    const { data: tenants, isLoading, error } = useGetList<Tenant>('tenant', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'name', order: 'ASC' }
    });
    
    const [selectedTenant, setSelectedTenant] = useState<string>('');

    const handleTenantChange = (event: SelectChangeEvent<string>) => {
        const tenantId = event.target.value;
        setSelectedTenant(tenantId);
    };

    const handleInsertClick = () => {
        const tenantToSelect = selectedTenant || null;
        onSelectTenant(tenantToSelect);
    };

    if (isLoading) {
        return <Box>Loading tenants...</Box>;
    }

    if (error) {
        return <Box color="error.main">Error loading tenants</Box>;
    }

    if (!tenants || tenants.length === 0) {
        return <Box>No tenants available</Box>;
    }

    return (
        <Box display="flex" alignItems="center" gap={1}>
            <FormControl variant={variant} size={size} sx={{ minWidth: 150 }}>
                <InputLabel id="tenant-selector-label" sx={{ fontSize: size === 'small' ? '0.75rem' : undefined }}>
                    {label}
                </InputLabel>
                <Select
                    labelId="tenant-selector-label"
                    value={selectedTenant}
                    onChange={handleTenantChange}
                    label={label}
                    disabled={disabled}
                    sx={{ 
                        height: size === 'small' ? '32px' : undefined,
                        '& .MuiSelect-select': {
                            padding: size === 'small' ? '4px 16px' : undefined,
                            fontSize: size === 'small' ? '0.75rem' : undefined
                        }
                    }}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {tenants.map((tenant) => (
                        <MenuItem key={tenant.id} value={tenant.id}>
                            {tenant.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Button
                variant="contained"
                onClick={handleInsertClick}
                disabled={disabled}
                size={size}
                sx={{
                    height: size === 'small' ? '28px' : undefined,
                    fontSize: size === 'small' ? '0.7rem' : undefined,
                    minWidth: size === 'small' ? '60px' : undefined
                }}
            >
                Insert
            </Button>
        </Box>
    );
};
